import React, { FunctionComponent, ReactNode } from 'react'

import { LinearGradient } from 'expo-linear-gradient'
import styled, { useTheme } from 'styled-components/native'

import { ThemeType, tID } from '../../utils'

export interface AuroraAwareBackgroundProps {
  children: ReactNode
  /** Height of any header/toolbar above this gradient, used to offset the gradient start point for seamless alignment */
  headerOffset?: number
  disableAuroraBackground?: boolean /** overrides isAuroraTheme to disable aurora background */
  fallbackBackground?: {
    component: React.ElementType
    props?: Record<string, any>
  }
}

const GradientContainer = styled.View({
  flexGrow: 1,
  position: 'relative',
})

const AbsoluteGradient = styled(LinearGradient)<{ headerOffset: number }>(({ headerOffset }) => ({
  position: 'absolute',
  top: headerOffset > 0 ? -headerOffset : 0,
  left: 0,
  right: 0,
  bottom: 0,
}))

const ContentWrapper = styled.View({
  flexGrow: 1,
})

/**
 * A wrapper component that applies an aurora-themed background gradient when the aurora theme is active,
 * and falls back to a provided background component or no background when not.
 */
export const AuroraAwareBackground: FunctionComponent<AuroraAwareBackgroundProps> = ({
  children,
  disableAuroraBackground = false,
  headerOffset = 0,
  fallbackBackground,
}) => {
  const {
    colors: { backgroundGradientFirst, backgroundGradientSecond },
    isAuroraTheme,
  } = useTheme() as ThemeType

  if (!isAuroraTheme || disableAuroraBackground) {
    if (fallbackBackground) {
      const { component: FallbackBackgroundComponent, props } = fallbackBackground
      return <FallbackBackgroundComponent {...props}>{children}</FallbackBackgroundComponent>
    }
    return <>{children}</>
  }

  const gradientColors = [backgroundGradientFirst, backgroundGradientSecond] as [string, string]

  return (
    <GradientContainer testID={tID('AuroraGradientBackground')}>
      <AbsoluteGradient
        headerOffset={headerOffset}
        colors={gradientColors}
        start={{ x: 0.5, y: 0 }}
        end={{ x: 0.5, y: 1 }}
        locations={[0, 1]}
      />
      <ContentWrapper>{children}</ContentWrapper>
    </GradientContainer>
  )
}
